import { useFormik } from 'formik';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import {
    signIn,
    signOut,
    currentSession,
    forgotPassword,
    forgotPasswordSubmit,
    completeNewPassword
} from 'aws-auth-lib';
import * as AmplifyAuth from 'aws-amplify/auth';
import * as Yup from 'yup';
import {
    hasRequiredSpecialChar,
    passwordPolicyMessage
} from '../utils/passwordPolicy';

const passwordValidationSchema = Yup.string()
    .max(255)
    .required('Password is required')
    .test('has-lowercase', passwordPolicyMessage, (value) =>
        typeof value === 'string' ? /[a-z]/.test(value) : false
    )
    .test('has-uppercase', passwordPolicyMessage, (value) =>
        typeof value === 'string' ? /[A-Z]/.test(value) : false
    )
    .test('has-number', passwordPolicyMessage, (value) =>
        typeof value === 'string' ? /\d/.test(value) : false
    )
    .test('has-special', passwordPolicyMessage, (value) =>
        hasRequiredSpecialChar(value)
    );

export const useSignIn = () => {
    const navigate = useNavigate();

    const formik = useFormik({
        initialValues: {
            email: '',
            password: '',
            submit: null
        },
        validationSchema: Yup.object().shape({
            email: Yup.string()
                .email('Must be a valid email')
                .max(255)
                .required('Email is required'),
            password: Yup.string().max(255).required('Password is required')
        }),
        onSubmit: async ({ email, password }, { setErrors, setSubmitting }) => {
            setSubmitting(true);
            try {
                const result = await signIn(email, password);

                if (result?.isSignedIn) {
                    navigate('/', { replace: true });
                    return;
                }

                navigate('/changePassword', { state: { nextStep: result?.nextStep } });
            } catch (error) {
                setErrors({ submit: 'Email ou password e invalido' });
            } finally {
                setSubmitting(false);
            }
        }
    });

    return formik;
};

export const usePasswordlessSignIn = () => {
    const formik = useFormik({
        initialValues: {
            email: '',
            code: '',
            otpRequested: false,
            submit: null
        },
        validationSchema: Yup.object().shape({
            email: Yup.string()
                .email('Tem de ser um email valido')
                .max(255)
                .required('Email e obrigatorio'),
            code: Yup.string().when('otpRequested', {
                is: true,
                then: (schema) =>
                    schema
                        .matches(/^\d{8}$/, 'O codigo deve ter 8 digitos')
                        .required('Codigo e obrigatorio'),
                otherwise: (schema) => schema.notRequired()
            })
        }),
        onSubmit: async (
            { email, code, otpRequested },
            { setErrors, setSubmitting, setFieldValue }
        ) => {
            setSubmitting(true);
            setErrors({ submit: null });

            try {
                if (!otpRequested) {
                    const result = await AmplifyAuth.signIn({
                        username: email,
                        options: {
                            authFlowType: 'USER_AUTH',
                            preferredChallenge: 'EMAIL_OTP'
                        }
                    });

                    const signInStep = result?.nextStep?.signInStep;
                    if (signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
                        await setFieldValue('otpRequested', true, false);
                        return;
                    }

                    if (result?.isSignedIn) {
                        await currentSession();
                        window.location.replace('/');
                        return;
                    }

                    setErrors({
                        submit: 'Nao foi possivel iniciar o login por codigo neste utilizador.'
                    });
                    return;
                }

                const confirmResult = await AmplifyAuth.confirmSignIn({
                    challengeResponse: code
                });

                if (
                    confirmResult?.isSignedIn ||
                    confirmResult?.nextStep?.signInStep === 'DONE'
                ) {
                    await currentSession();
                    window.location.replace('/');
                    return;
                }

                setErrors({
                    submit: 'O codigo foi aceite mas o login nao ficou concluido.'
                });
            } catch (error) {
                const message = error?.name || error?.code;

                if (message === 'CodeMismatchException') {
                    setErrors({ submit: 'O codigo introduzido e invalido.' });
                } else if (message === 'ExpiredCodeException') {
                    setErrors({ submit: 'O codigo expirou. Peca um novo codigo.' });
                } else if (message === 'UserNotFoundException') {
                    setErrors({ submit: 'Nao existe nenhum utilizador com esse email.' });
                } else if (message === 'AuthValidationError') {
                    setErrors({ submit: 'Preencha os campos obrigatorios.' });
                } else {
                    setErrors({
                        submit: otpRequested
                            ? 'Nao foi possivel validar o codigo.'
                            : 'Nao foi possivel enviar o codigo de acesso.'
                    });
                }
            } finally {
                setSubmitting(false);
            }
        }
    });

    const resendCode = async () => {
        const errors = await formik.validateForm();
        if (errors?.email) {
            await formik.setFieldTouched('email', true, false);
            return;
        }

        formik.setSubmitting(true);
        formik.setErrors({ submit: null });

        try {
            await AmplifyAuth.signIn({
                username: formik.values.email,
                options: {
                    authFlowType: 'USER_AUTH',
                    preferredChallenge: 'EMAIL_OTP'
                }
            });
        } catch (error) {
            formik.setErrors({
                submit: 'Nao foi possivel reenviar o codigo.'
            });
        } finally {
            formik.setSubmitting(false);
        }
    };

    const resetFlow = () => {
        formik.resetForm();
    };

    const goBackToEmailStep = async () => {
        await formik.setFieldValue('code', '', false);
        await formik.setFieldValue('otpRequested', false, false);
        formik.setErrors({ submit: null });
    };

    return {
        ...formik,
        resendCode,
        resetFlow,
        goBackToEmailStep
    };
};

export const useForgetPassword = () => {
    const navigate = useNavigate();

    const formik = useFormik({
        initialValues: {
            email: '',
            submit: null
        },
        validationSchema: Yup.object().shape({
            email: Yup.string()
                .email('Must be a valid email')
                .max(255)
                .required('Email is required')
        }),
        onSubmit: ({ email }, { setErrors, setSubmitting }) => {
            setSubmitting(true);
            forgotPassword(email)
                .then(() => {
                    navigate(
                        `/forgetPassword/changePassword?email=${encodeURIComponent(email)}`,
                        { state: { email } }
                    );
                })
                .catch(() => {
                    setErrors({ submit: 'Erro na recuperacao da palavra-passe' });
                })
                .finally(() => {
                    setSubmitting(false);
                });
        }
    });

    return formik;
};

export const useForgetPasswordSubmit = (emailSeed) => {
    const navigate = useNavigate();

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            email: emailSeed || '',
            validationCode: '',
            newPassword: '',
            confirmPassword: '',
            submit: null
        },
        validationSchema: Yup.object().shape({
            email: Yup.string()
                .email('Must be a valid email')
                .max(255)
                .required('Email is required'),
            validationCode: Yup.string().required('Validation Code is required'),
            newPassword: passwordValidationSchema,
            confirmPassword: Yup.string().oneOf(
                [Yup.ref('newPassword'), null],
                'As passwords devem coincidir'
            )
        }),
        onSubmit: async (
            { email, validationCode, newPassword },
            { setErrors, setSubmitting }
        ) => {
            setSubmitting(true);
            try {
                await forgotPasswordSubmit(email, validationCode, newPassword);
                navigate('/login');
            } catch (error) {
                setErrors({ submit: 'Erro a definir nova palavra-passe' });
            } finally {
                setSubmitting(false);
            }
        }
    });

    return formik;
};

export const useChangePassword = (state) => {
    const navigate = useNavigate();
    const location = useLocation();
    const nextState = state ?? location.state;

    const formik = useFormik({
        initialValues: {
            newPassword: '',
            confirmPassword: '',
            submit: null
        },
        validationSchema: Yup.object().shape({
            newPassword: passwordValidationSchema,
            confirmPassword: Yup.string().oneOf(
                [Yup.ref('newPassword'), null],
                'As passwords devem coincidir'
            )
        }),
        onSubmit: async ({ newPassword }, { setErrors, setSubmitting }) => {
            setSubmitting(true);
            try {
                await completeNewPassword(newPassword, nextState);
                navigate('/login');
            } catch (error) {
                setErrors({ submit: 'Erro a alterar palavra-passe' });
            } finally {
                setSubmitting(false);
            }
        }
    });

    return formik;
};

export const useCompleteNewPassword = (state) => useChangePassword(state);

export const useSignOut = () => {
    const navigate = useNavigate();

    return async () => {
        await signOut();
        navigate('/login', { replace: true });
    };
};



